import { Box } from "@mui/material";
import { useRef, useEffect, useMemo } from "react";
import {
  RichTextEditor,
  RichTextReadOnly,
  TableBubbleMenu,
  type RichTextEditorRef,
} from "mui-tiptap";
import { LinkBubbleMenu } from "./LinkBubbleMenu";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";
import { CollapsableBubbleMenu } from "./CollapsableBubbleMenu";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@fluentui/react-components";
import { Tool } from "../../models/Tool";
import { SkanskaColorTokens } from "../../style/colors";
import { normalizeBoldTextColor } from "./textFormat";

type PropTypes = {
  content: string;
  onChange: (value: string) => void;
  disabled: boolean;
  onLinkToolInsert?: (tool: Tool) => void;
  onLinkToolRemove?: (toolId: string) => void;
  selectedTools?: Tool[];
  resolvedImages?: Map<string, string>;
};

const useStyles = makeStyles({
  editorContainer: {
    "& .MuiTiptap-RichTextContent-root": {
      maxHeight: "70vh",
      overflow: "auto",
    },
    // Link color styling
    "& .ProseMirror a": {
      color: SkanskaColorTokens.color_primary_blue + " !important",
      pointerEvents: "none",
      cursor: "default",
    },
    // Removed tool links
    "& .ProseMirror span[style*='color: #666']": {
      color: "#666 !important",
    },
    // Re-enable pointer events for link text content
    "& .ProseMirror a:not(:has(img))": {
      pointerEvents: "auto",
      cursor: "pointer",
    },
    // Re-enable pointer events for images inside links so they can be selected
    "& .ProseMirror a img": {
      pointerEvents: "auto",
      cursor: "pointer",
      transition: "box-shadow 0.2s ease",
    },
    "& .ProseMirror a:hover img": {
      boxShadow: "0 0 0 2px #0078d4",
    },
  },
});

function replaceImageSources(
  html: string,
  resolvedImages: Map<string, string>
): string {
  if (resolvedImages.size === 0) return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const imgs = doc.querySelectorAll("img[data-image-id]");
  imgs.forEach((img) => {
    const imageId = img.getAttribute("data-image-id");
    if (imageId && resolvedImages.has(imageId)) {
      img.setAttribute("src", resolvedImages.get(imageId)!);
    }
  });
  return doc.body.innerHTML;
}

const MUIEditor = ({
  content,
  onChange,
  disabled,
  onLinkToolInsert,
  onLinkToolRemove,
  selectedTools,
  resolvedImages = new Map(),
}: PropTypes) => {
  const { t } = useTranslation();
  const extensions = useExtensions({
    placeholder: t("activityDescription"),
  });
  const rteRef = useRef<RichTextEditorRef>(null);
  const classes = useStyles();

  const resolvedContent = useMemo(
    () => replaceImageSources(normalizeBoldTextColor(content), resolvedImages),
    [content, resolvedImages]
  );

  // Update editor content when it changes externally
  // example: when deleting a tool from GroupedToolList
  useEffect(() => {
    if (rteRef.current?.editor && !disabled) {
      const currentContent = rteRef.current.editor.getHTML();
      if (currentContent !== resolvedContent) {
        rteRef.current.editor.commands.setContent(resolvedContent);
      }
    }
  }, [resolvedContent, disabled]);

  return (
    <>
      <Box className={classes.editorContainer}>
        {!disabled ? (
          <RichTextEditor
            ref={rteRef}
            extensions={extensions}
            content={resolvedContent}
            onUpdate={(e) => onChange(e.editor.getHTML())}
            editorProps={{}}
            renderControls={() => (
              <EditorMenuControls
                onLinkToolInsert={onLinkToolInsert}
                onLinkRemove={onLinkToolRemove}
                selectedTools={selectedTools}
              />
            )}
            RichTextFieldProps={{
              variant: "outlined",
            }}
          >
            {() => (
              <>
                <CollapsableBubbleMenu />
                <LinkBubbleMenu />
                <TableBubbleMenu />
              </>
            )}
          </RichTextEditor>
        ) : (
          <RichTextReadOnly extensions={extensions} content={resolvedContent} />
        )}
      </Box>
    </>
  );
};

export default MUIEditor;
